import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useTheme } from "next-themes";
import { Toaster } from "./components/ui/sonner";
import { useAuth } from "./context/AuthContext";
import { PreferencesProvider } from "./context/PreferencesContext";
import Landing from "./pages/Landing";
import AuthPage from "./pages/Auth";
import Lobby from "./pages/Lobby";
import GameLocal from "./pages/GameLocal";
import GameAI from "./pages/GameAI";
import GameOnline from "./pages/GameOnline";
import Leaderboard from "./pages/Leaderboard";
import Profile from "./pages/Profile";
import Rules from "./pages/Rules";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DataDeletion from "./pages/DataDeletion";
import OnlineRooms from "./pages/OnlineRooms";
import Replay from "./pages/Replay";
import Tutorial from "./pages/Tutorial";
import Setup from "./pages/Setup";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminLeaderboard from "./pages/admin/AdminLeaderboard";
import AdminBoard from "./pages/admin/AdminBoard";
import AuthCallbackHandler from "./components/AuthCallbackHandler";

const PAGE_TITLE = "Fa'anu'u - TAHITI";

function DocumentTitle() {
  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);
  return null;
}

function ThemeSync() {
  const { user } = useAuth();
  const { setTheme } = useTheme();
  useEffect(() => {
    const ui = user?.preferences?.ui_theme;
    if (ui && ["dark", "light", "system"].includes(ui)) {
      setTheme(ui);
    }
  }, [user, setTheme]);
  return null;
}

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-tribal">
        <div className="text-[color:var(--text-secondary)] tracking-[0.2em] text-sm">CHARGEMENT…</div>
      </div>
    );
  }
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?next=${next}`} replace />;
  }
  return children;
}

function App() {
  return (
    <PreferencesProvider>
      <BrowserRouter>
        <DocumentTitle />
        <ThemeSync />
        <AuthCallbackHandler />
        <div className="min-h-screen bg-tribal">
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/rules" element={<Rules />} />
            <Route path="/politique-confidentialite" element={<PrivacyPolicy />} />
            <Route path="/suppression-donnees" element={<DataDeletion />} />
            <Route path="/lobby" element={<Lobby />} />
            <Route path="/game/local" element={<GameLocal />} />
            <Route path="/game/ai" element={<GameAI />} />
            <Route path="/game/online/:roomId" element={<Protected><GameOnline /></Protected>} />
            <Route path="/online/rooms" element={<Protected><OnlineRooms /></Protected>} />
            <Route path="/replay/:gameId" element={<Protected><Replay /></Protected>} />
            <Route path="/tutorial" element={<Tutorial />} />
            <Route path="/setup" element={<Setup />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/admin" element={<Protected><AdminLayout /></Protected>}>
              <Route index element={<Navigate to="/admin/users" replace />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="classement" element={<AdminLeaderboard />} />
              <Route path="plateau" element={<AdminBoard />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </div>
      </BrowserRouter>
    </PreferencesProvider>
  );
}

export default App;
